/** 与 uploadWorkspace 页一致：object-fit:contain 内实际画面矩形与 ROI 约束 */

/** 与 SubtitleOverlayPlayer 预览一致：设计稿参考与绘制常数（持久化应基于比例，不直接存预览 px） */
export const SUBTITLE_PREVIEW_REFERENCE_WIDTH = 1920;
export const SUBTITLE_PREVIEW_REFERENCE_HEIGHT = 1080;
export const SUBTITLE_PREVIEW_BASE_FONT_PX = 130;
/**
 * 横屏源：预览 canvas 相对服务端压制约偏大 2 倍时的校正系数（与 `getSubtitleBurnVisualScale` 联用）。
 * 竖屏源不乘此系数（未校正前已与压制一致）。
 */
export const SUBTITLE_PREVIEW_BURN_VISUAL_SCALE_LANDSCAPE = 0.5;

/**
 * 横屏源 ×{@link SUBTITLE_PREVIEW_BURN_VISUAL_SCALE_LANDSCAPE}，竖屏源 ×1。
 * 优先用源视频像素方向；元数据未就绪时用 contain 矩形宽高比兜底。
 */
export function getSubtitleBurnVisualScale(
  contentW: number,
  contentH: number,
  intrinsicVideoWidth?: number,
  intrinsicVideoHeight?: number
): number {
  if (
    intrinsicVideoWidth != null &&
    intrinsicVideoHeight != null &&
    intrinsicVideoWidth > 0 &&
    intrinsicVideoHeight > 0
  ) {
    return intrinsicVideoWidth >= intrinsicVideoHeight
      ? SUBTITLE_PREVIEW_BURN_VISUAL_SCALE_LANDSCAPE
      : 1;
  }
  if (contentW > 0 && contentH > 0) {
    return contentW >= contentH ? SUBTITLE_PREVIEW_BURN_VISUAL_SCALE_LANDSCAPE : 1;
  }
  return 1;
}
/** 字幕框相对 contain 画面宽、高的占比；与 max 文本宽占比一致 */
export const SUBTITLE_PREVIEW_BOX_WIDTH_FRAC = 0.85;
export const SUBTITLE_PREVIEW_BOX_HEIGHT_FRAC = 0.15;
export const SUBTITLE_PREVIEW_STROKE_TO_FONT_FRAC = 0.15;
export const SUBTITLE_PREVIEW_SCALE_MIN = 0.2;
export const SUBTITLE_PREVIEW_SCALE_MAX = 3;
export const SUBTITLE_PREVIEW_MIN_DRAW_FONT_PX = 3;
/** 预览单行字高 = fontSize × 该系数（与 SubtitleOverlayPlayer 一致） */
export const SUBTITLE_PREVIEW_LINE_HEIGHT_RATIO = 1.4;
export const SUBTITLE_DEFAULT_LINE_NUMBER = 2;
export const SUBTITLE_LINE_NUMBER_MIN = 1;
export const SUBTITLE_LINE_NUMBER_MAX = 5;
/** 编辑页 UI 调节行数上限 */
export const SUBTITLE_LINE_NUMBER_ADJUST_MAX = 4;

/** GET /video/task/get 的 subtitleLineNumber；0 或未下发时默认 2 行 */
export function normalizeSubtitleLineNumber(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return SUBTITLE_DEFAULT_LINE_NUMBER;
  return Math.max(SUBTITLE_LINE_NUMBER_MIN, Math.min(SUBTITLE_LINE_NUMBER_MAX, Math.round(n)));
}

/** 编辑页箭头调节：1～4 行 */
export function clampSubtitleAdjustableLineNumber(value: number): number {
  return Math.max(
    SUBTITLE_LINE_NUMBER_MIN,
    Math.min(SUBTITLE_LINE_NUMBER_ADJUST_MAX, Math.round(value))
  );
}

/**
 * 由固定行数框高反推 fontScale，与 computeFixedSubtitleBoxHeightPx / 角点缩放互逆。
 */
export function deriveSubtitleFontScaleFromBoxHeight(
  boxHeightPx: number,
  lineCount: number,
  contentW: number,
  contentH: number,
  verticalPaddingPx = 0,
  intrinsicVideoWidth?: number,
  intrinsicVideoHeight?: number
): number {
  const lines = normalizeSubtitleLineNumber(lineCount);
  if (!(boxHeightPx > 0) || !(contentH > 0) || !(contentW > 0)) {
    return SUBTITLE_PREVIEW_SCALE_MIN;
  }
  const lineBlock = lines * SUBTITLE_PREVIEW_LINE_HEIGHT_RATIO;
  const fontSize = Math.max(
    SUBTITLE_PREVIEW_MIN_DRAW_FONT_PX,
    (boxHeightPx - verticalPaddingPx * 2) / lineBlock
  );
  const fontSizeRatio = fontDrawPxToFontSizeRatio(fontSize, contentH);
  return inferScaleFromFontSizeRatio(
    fontSizeRatio,
    contentW,
    contentH,
    SUBTITLE_PREVIEW_SCALE_MIN,
    SUBTITLE_PREVIEW_SCALE_MAX,
    intrinsicVideoWidth,
    intrinsicVideoHeight
  );
}

/** 角点缩放 lockAspectRatio：模型宽 / 模型高 */
export function computeSubtitleCornerLockAspect(
  boxWidthPx: number,
  boxHeightPx: number
): number | null {
  if (!(boxWidthPx > 0) || !(boxHeightPx > 0)) return null;
  return boxWidthPx / boxHeightPx;
}

/** scale + 行数 → 固定行数框高（与 SubtitleOverlayPlayer 展示一致） */
export function computeFixedSubtitleBoxHeightFromScale(params: {
  scale: number;
  lineCount: number;
  contentW: number;
  contentH: number;
  verticalPaddingPx?: number;
  intrinsicVideoWidth?: number;
  intrinsicVideoHeight?: number;
}): number {
  const fontSize = computePreviewSubtitleDrawFontPx({
    scale: params.scale,
    contentW: params.contentW,
    contentH: params.contentH,
    intrinsicVideoWidth: params.intrinsicVideoWidth,
    intrinsicVideoHeight: params.intrinsicVideoHeight,
  });
  return computeFixedSubtitleBoxHeightPx(
    fontSize,
    params.lineCount,
    params.verticalPaddingPx ?? 0
  );
}

/** 字幕框固定高度：lineCount 行 × 单行字高 + 上下 padding */
export function computeFixedSubtitleBoxHeightPx(
  fontSize: number,
  lineCount: number,
  verticalPaddingPx = 0
): number {
  const lines = normalizeSubtitleLineNumber(lineCount);
  return fontSize * SUBTITLE_PREVIEW_LINE_HEIGHT_RATIO * lines + verticalPaddingPx * 2;
}

/** 实际换行后的文字块高度（供后端顶对齐补偿，不含额外空行） */
export function computeWrappedSubtitleTextBlockHeightPx(
  fontSize: number,
  wrappedLineCount: number,
  verticalPaddingPx = 0
): number {
  const lines = Math.max(1, Math.round(wrappedLineCount));
  return fontSize * SUBTITLE_PREVIEW_LINE_HEIGHT_RATIO * lines + verticalPaddingPx * 2;
}

/** 框宽占画面宽的可调范围（左右手柄） */
export const SUBTITLE_BOX_WIDTH_RATIO_MIN = 0.18;
export const SUBTITLE_BOX_WIDTH_RATIO_MAX = 0.98;
/** 超长单词等导致的内容最小框宽上限（相对 contain 画面宽），防止手柄超出画面 */
export const SUBTITLE_CONTENT_MIN_BOX_WIDTH_MAX_FRAC = 0.9;

/** 与 SubtitleOverlayPlayer 一致：字幕文字与框左右边的最小间距（px） */
export const SUBTITLE_BOX_MIN_PADDING_X = 8;
export const SUBTITLE_BOX_HORIZONTAL_PADDING_RATIO = 0.8;

export function computeSubtitleBoxPadding(fontSize: number): {
  paddingX: number;
  paddingY: number;
} {
  return {
    paddingX: Math.max(
      SUBTITLE_BOX_MIN_PADDING_X,
      fontSize * SUBTITLE_BOX_HORIZONTAL_PADDING_RATIO
    ),
    paddingY: 0,
  };
}

export function clampSubtitleBoxWidthRatio(r: number): number {
  return Math.max(SUBTITLE_BOX_WIDTH_RATIO_MIN, Math.min(SUBTITLE_BOX_WIDTH_RATIO_MAX, r));
}

/**
 * 检测字符串是否包含 CJK（中日韩）字符
 * CJK 字符可以在任意位置换行，而不需要依赖空格
 */
function containsCJK(text: string): boolean {
  return /[一-龥぀-ゟ゠-ヿ가-힯㐀-䶿豈-﫿]/.test(text);
}

/**
 * 按宽度拆分 CJK 字符串（允许在任意字符间换行）
 * 用于处理中文、日文、韩文等无空格分隔的语言
 * maxLines: 最多拆分为几行（受行数限制）
 */
function splitCJKString(
  text: string,
  maxWidth: number,
  measureTextWidth: (line: string) => number,
  maxLines: number = Infinity
): string[] {
  const lines: string[] = [];
  let currentLine = '';

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const testLine = currentLine + char;

    if (measureTextWidth(testLine) <= maxWidth) {
      currentLine = testLine;
    } else {
      // 当前行已满，换行
      if (currentLine) {
        // 已达行数上限，剩余字符全部拼入当前行（截断后续拆分）
        if (lines.length + 1 >= maxLines) {
          currentLine += text.slice(i);
          break;
        }
        lines.push(currentLine);
      }
      currentLine = char;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

/**
 * 字幕文本换行函数：支持多语种智能换行
 *
 * 策略：
 * 1. 英文/西语等印欧语系：按空格分词，保持单词完整性
 * 2. 中文/日文/韩文等 CJK 语系：允许在任意字符间换行，但受 maxLines 限制
 * 3. 混合语种：先按空格分词，再对超宽的 CJK token 按字符拆分
 *
 * @param maxLines 最多输出几行（受字幕行数控制）。默认 Infinity 表示不限制。
 *                 传入行数后，CJK 拆分不会超过此行数——超出部分截断到最后一行。
 * measureTextWidth 由调用方在设置好 ctx.font 后提供。
 */
export function wrapSubtitleTextByTokens(
  text: string,
  maxWidth: number,
  measureTextWidth: (line: string) => number,
  maxLines: number = Infinity
): string[] {
  if (maxWidth <= 0) return [];

  // 按空格分词（保留空格 token 用于后续处理）
  const tokens = text.split(/(\s+)/);
  const lines: string[] = [];
  let currentLine = '';

  const pushLine = () => {
    const trimmed = currentLine.trim();
    if (trimmed) lines.push(trimmed);
    currentLine = '';
  };

  for (const token of tokens) {
    if (!token) continue;

    // 已达行数上限，剩余 token 全部拼入当前行
    if (lines.length >= maxLines) {
      currentLine += (currentLine.trim() ? ' ' : '') + token;
      continue;
    }

    // 处理空格 token
    if (/^\s+$/.test(token)) {
      if (currentLine.trim()) currentLine += ' ';
      continue;
    }

    const testLine = currentLine + (currentLine.trim() ? ' ' : '') + token;

    // 情况 1：整个 token 能放下，直接添加
    if (measureTextWidth(testLine) <= maxWidth) {
      currentLine = testLine;
      continue;
    }

    // 情况 2：token 放不下，需要换行
    if (currentLine.trim()) {
      // 推入当前行后 lines.length + 1（currentLine 末尾还会推一次）会超过 maxLines，
      // 则不推入，将当前 token 拼入 currentLine
      if (lines.length + 1 >= maxLines) {
        currentLine += (currentLine.trim() ? ' ' : '') + token;
        continue;
      }
      pushLine();
    }

    // 情况 3：token 是 CJK 字符且超宽，按字符拆分（受剩余行数限制）
    if (containsCJK(token) && measureTextWidth(token) > maxWidth) {
      const remainingLines = maxLines - lines.length;
      const charLines = splitCJKString(token, maxWidth, measureTextWidth, remainingLines);
      // 前 N-1 行直接推入 lines，最后一行作为 currentLine（可能与后续 token 拼接）
      if (charLines.length > 0) {
        for (let i = 0; i < charLines.length - 1; i++) {
          lines.push(charLines[i]);
        }
        currentLine = charLines[charLines.length - 1];
      }
    } else {
      // 非 CJK token 或 CJK token 未超宽，直接作为新行的开始
      currentLine = token;
    }
  }

  if (currentLine.trim()) {
    lines.push(currentLine.trim());
  }

  return lines;
}

export function countSubtitleWrapLines(
  text: string,
  maxTextWidthPx: number,
  measureTextWidth: (line: string) => number
): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return wrapSubtitleTextByTokens(trimmed, maxTextWidthPx, measureTextWidth).length;
}

/** 最长非空白 token 的绘制宽（整词不拆行时的硬下限） */
export function maxSubtitleTokenWidthPx(
  text: string,
  measureTextWidth: (line: string) => number
): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  let max = 0;
  for (const token of trimmed.split(/(\s+)/)) {
    if (!token || /^\s+$/.test(token)) continue;
    max = Math.max(max, measureTextWidth(token));
  }
  return max;
}

/**
 * 行数 ≤ lineCap，且每行实际绘制宽 ≤ maxTextWidthPx（避免整词独占一行却超出框宽）。
 */
export function isSubtitleWrapLayoutValid(
  text: string,
  maxTextWidthPx: number,
  lineCap: number,
  measureTextWidth: (line: string) => number
): boolean {
  const trimmed = text.trim();
  if (!trimmed) return true;
  if (maxTextWidthPx <= 0) return false;
  const cap = normalizeSubtitleLineNumber(lineCap);
  const lines = wrapSubtitleTextByTokens(trimmed, maxTextWidthPx, measureTextWidth, cap);
  if (lines.length > cap) return false;
  for (const line of lines) {
    if (measureTextWidth(line) > maxTextWidthPx + 0.5) return false;
  }
  return true;
}

/**
 * 在固定行数上限下，当前文案允许的最窄字幕框宽（px，含左右 padding）。
 * 与预览绘制共用 wrapSubtitleTextByTokens；结果会夹在 [globalMin, contentW×0.9]。
 */
export function computeMinSubtitleBoxWidthPx(params: {
  text: string;
  lineCap: number;
  fontSizePx: number;
  contentW: number;
  measureTextWidth: (line: string) => number;
}): number {
  const { text, fontSizePx, contentW, measureTextWidth } = params;
  const trimmed = text.trim();
  const globalMinBoxW = Math.max(40, contentW * SUBTITLE_BOX_WIDTH_RATIO_MIN);
  if (!trimmed || contentW <= 0 || fontSizePx <= 0) {
    return globalMinBoxW;
  }

  const cap = normalizeSubtitleLineNumber(params.lineCap);
  const { paddingX } = computeSubtitleBoxPadding(fontSizePx);
  const hardMaxBoxW = contentW * SUBTITLE_CONTENT_MIN_BOX_WIDTH_MAX_FRAC;
  const maxInner = Math.max(1, hardMaxBoxW - paddingX * 2);

  // CJK 文本：最小宽度 = 单字符宽度，与行数完全无关
  // 行数只控制换行显示，不影响框的缩放范围
  if (containsCJK(trimmed) && !/\s/.test(trimmed)) {
    const totalTextWidth = measureTextWidth(trimmed);
    const totalChars = trimmed.length;
    const charWidth = totalChars > 0 ? Math.ceil(totalTextWidth / totalChars) : Math.ceil(totalTextWidth);
    const boxW = charWidth + paddingX * 2;
    return Math.max(globalMinBoxW, Math.min(hardMaxBoxW, boxW));
  }

  const wordMinInner = Math.max(1, Math.ceil(maxSubtitleTokenWidthPx(trimmed, measureTextWidth)));

  if (!isSubtitleWrapLayoutValid(trimmed, maxInner, cap, measureTextWidth)) {
    return Math.min(hardMaxBoxW, contentW * SUBTITLE_BOX_WIDTH_RATIO_MAX);
  }

  let lo = wordMinInner;
  let hi = maxInner;
  if (isSubtitleWrapLayoutValid(trimmed, lo, cap, measureTextWidth)) {
    const boxW = lo + paddingX * 2;
    return Math.max(globalMinBoxW, Math.min(hardMaxBoxW, boxW));
  }

  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (isSubtitleWrapLayoutValid(trimmed, mid, cap, measureTextWidth)) {
      hi = mid;
    } else {
      lo = mid + 1;
    }
  }

  const boxW = lo + paddingX * 2;
  return Math.max(globalMinBoxW, Math.min(hardMaxBoxW, boxW));
}

/** 左右/角点缩放时把框宽限制在全局与内容最小宽之间 */
export function clampSubtitleBoxWidthPx(
  rawBoxWPx: number,
  contentW: number,
  contentMinBoxWPx: number
): number {
  if (contentW <= 0) return Math.max(40, rawBoxWPx);
  const globalMin = Math.max(40, Math.round(contentW * SUBTITLE_BOX_WIDTH_RATIO_MIN));
  const minW = Math.max(globalMin, contentMinBoxWPx);
  const maxW = contentW * SUBTITLE_BOX_WIDTH_RATIO_MAX;
  return Math.max(minW, Math.min(maxW, rawBoxWPx));
}

export function previewSubtitleFontScaleFactor(contentW: number, contentH: number): number {
  return Math.min(
    contentW / SUBTITLE_PREVIEW_REFERENCE_WIDTH,
    contentH / SUBTITLE_PREVIEW_REFERENCE_HEIGHT
  );
}

/** 当前预览下实际绘制字号（px），与 SubtitleOverlayPlayer 内公式一致 */
export function computePreviewSubtitleDrawFontPx(params: {
  scale: number;
  contentW: number;
  contentH: number;
  intrinsicVideoWidth?: number;
  intrinsicVideoHeight?: number;
}): number {
  const { scale, contentW, contentH, intrinsicVideoWidth, intrinsicVideoHeight } = params;
  const burnVisual = getSubtitleBurnVisualScale(contentW, contentH, intrinsicVideoWidth, intrinsicVideoHeight);
  const raw =
    SUBTITLE_PREVIEW_BASE_FONT_PX * scale * previewSubtitleFontScaleFactor(contentW, contentH) * burnVisual;
  return Math.max(SUBTITLE_PREVIEW_MIN_DRAW_FONT_PX, raw);
}

/**
 * 当前预览字号 / 当前 contain 画面显示高度。
 * 与视口无关：同一比例在任意预览缩放下一致；后端烧录可用 font_px = ratio * 目标视频像素高度。
 */
export function fontDrawPxToFontSizeRatio(drawFontPx: number, contentDisplayHeight: number): number {
  if (contentDisplayHeight <= 0) return 0;
  return drawFontPx / contentDisplayHeight;
}

export function fontSizeRatioToDrawFontPx(
  fontSizeRatio: number,
  contentDisplayHeight: number
): number {
  return Math.max(SUBTITLE_PREVIEW_MIN_DRAW_FONT_PX, fontSizeRatio * contentDisplayHeight);
}

/** 与 SubtitleOverlayPlayer 一致：预览区实际描边宽（px） */
export function computeSubtitlePreviewStrokePx(
  fontSizeRatio: number,
  contentDisplayHeight: number
): number {
  if (contentDisplayHeight <= 0) return 1;
  const drawFontPx = fontSizeRatioToDrawFontPx(fontSizeRatio, contentDisplayHeight);
  return Math.max(0.5, drawFontPx * SUBTITLE_PREVIEW_STROKE_TO_FONT_FRAC);
}

/** 合成接口 borderWidth：与预览描边一致，不用 canvas 全分辨率字号推算 */
export function computeGenerateVideoBorderWidth(
  fontSizeRatio: number,
  contentDisplayHeight: number
): number {
  return Math.max(1, Math.round(computeSubtitlePreviewStrokePx(fontSizeRatio, contentDisplayHeight)));
}

export function boxRatiosFromScale(scale: number): { widthRatio: number; heightRatio: number } {
  return {
    widthRatio: SUBTITLE_PREVIEW_BOX_WIDTH_FRAC * scale,
    heightRatio: SUBTITLE_PREVIEW_BOX_HEIGHT_FRAC * scale,
  };
}

export function scaleFromBoxWidthRatio(widthRatio: number): number {
  return widthRatio / SUBTITLE_PREVIEW_BOX_WIDTH_FRAC;
}

/**
 * 从已持久化的 fontSizeRatio 反推当前预览用的 scale（与 computePreviewSubtitleDrawFontPx 互逆）。
 */
export function inferScaleFromFontSizeRatio(
  fontSizeRatio: number,
  contentW: number,
  contentH: number,
  clampMin: number = SUBTITLE_PREVIEW_SCALE_MIN,
  clampMax: number = SUBTITLE_PREVIEW_SCALE_MAX,
  intrinsicVideoWidth?: number,
  intrinsicVideoHeight?: number
): number {
  const burnVisual = getSubtitleBurnVisualScale(contentW, contentH, intrinsicVideoWidth, intrinsicVideoHeight);
  const denom =
    SUBTITLE_PREVIEW_BASE_FONT_PX * previewSubtitleFontScaleFactor(contentW, contentH) * burnVisual;
  if (denom <= 0) return 1;
  const raw = (fontSizeRatio * contentH) / denom;
  return Math.max(clampMin, Math.min(clampMax, raw));
}

/** 提交后端 / 落库用的与分辨率无关的快照 */
export interface SubtitleStyleBackendPayload {
  FontSize: number;
  PosX: number;
  PosY: number;
  /** 视频原始宽（画布坐标系） */
  width: number;
  /** 视频原始高（画布坐标系） */
  height: number;
  /** 字幕框宽（视频像素） */
  boxWidth: number;
  /** 字幕框高（视频像素） */
  boxHeight: number;
}

/**
 * 将比例快照转为后端要求的绝对 px（基于视频 intrinsic 宽高）。
 * FontSize = fontSizeRatio × 视频高；Pos 由 relativeCenter 与框宽高还原左上角。
 */
export function buildSubtitleStyleBackendPayload(
  snapshot: SubtitleStylePersistSnapshot
): SubtitleStyleBackendPayload {
  const vW = snapshot.intrinsicVideo.width;
  const vH = snapshot.intrinsicVideo.height;
  const { fontSizeRatio, widthRatio, heightRatio, relativeCenter } = snapshot;

  const FontSize = Math.round(vH * fontSizeRatio);
  const boxWidth = Math.round(vW * widthRatio);
  const boxHeight = Math.round(vH * heightRatio);
  const PosX = Math.max(0, Math.min(vW - boxWidth, Math.round(relativeCenter.x * vW - boxWidth / 2)));
  const PosY = Math.max(0, Math.min(vH - boxHeight, Math.round(relativeCenter.y * vH - boxHeight / 2)));

  return {
    FontSize,
    PosX,
    PosY,
    width: vW,
    height: vH,
    boxWidth,
    boxHeight,
  };
}

/** 预览区相对原片的缩放系数：contain 画面宽 / 视频原始宽 */
export function computeSubtitlePreviewScale(
  contentDisplayWidth: number,
  intrinsicVideoWidth: number
): number {
  if (!(contentDisplayWidth > 0) || !(intrinsicVideoWidth > 0)) return 1;
  return contentDisplayWidth / intrinsicVideoWidth;
}

/** POST /video/subtitles/generate-video 请求体 */
export interface GenerateVideoRequestPayload {
  taskId: number;
  enabled: boolean;
  fontSize: number;
  fontColor: string;
  borderColor: string;
  borderWidth: number;
  boxX1: number;
  boxY1: number;
  boxX2: number;
  boxY2: number;
  canvasWidth: number;
  canvasHeight: number;
  videoWidth: number;
  videoHeight: number;
}

export const DEFAULT_GENERATE_VIDEO_FONT_COLOR = 'white';
export const DEFAULT_GENERATE_VIDEO_BORDER_COLOR = 'black';

/**
 * 由预览选框实际像素尺寸换算 canvas 坐标（与 Rnd 显示一致）。
 * 中心点 relativeCenter 相对 contain 画面 0~1；选框宽高与 contentW/H 同坐标系。
 */
export function buildSubtitleBoxCanvasRectFromPreview(params: {
  relativeCenter: { x: number; y: number };
  previewBoxWidthPx: number;
  previewBoxHeightPx: number;
  contentW: number;
  contentH: number;
  canvasW: number;
  canvasH: number;
}): {
  boxX1: number;
  boxY1: number;
  boxX2: number;
  boxY2: number;
  boxWidth: number;
  boxHeight: number;
} {
  const {
    relativeCenter,
    previewBoxWidthPx,
    previewBoxHeightPx,
    contentW,
    contentH,
    canvasW,
    canvasH,
  } = params;
  if (contentW <= 0 || contentH <= 0 || canvasW <= 0 || canvasH <= 0) {
    return { boxX1: 0, boxY1: 0, boxX2: 0, boxY2: 0, boxWidth: 0, boxHeight: 0 };
  }

  const scaleX = canvasW / contentW;
  const scaleY = canvasH / contentH;
  const boxWidth = Math.max(1, Math.round(previewBoxWidthPx * scaleX));
  const boxHeight = Math.max(1, Math.round(previewBoxHeightPx * scaleY));

  let boxX1 = Math.round(relativeCenter.x * canvasW - boxWidth / 2);
  let boxY1 = Math.round(relativeCenter.y * canvasH - boxHeight / 2);
  boxX1 = Math.max(0, Math.min(boxX1, canvasW - boxWidth));
  boxY1 = Math.max(0, Math.min(boxY1, canvasH - boxHeight));

  return {
    boxX1,
    boxY1,
    boxX2: boxX1 + boxWidth,
    boxY2: boxY1 + boxHeight,
    boxWidth,
    boxHeight,
  };
}

/**
 * 后端按 box 顶边顶对齐绘制时，将「展示用外框」换算为「压制用文字块」坐标。
 * 预览文字在外框内垂直居中，需把 boxY1 下移 (外框高 - 文字块高)/2。
 */
export function compensateBurnBoxForBackendTopAlign(params: {
  frameRect: { boxX1: number; boxY1: number; boxX2: number; boxY2: number };
  previewBoxHeightPx: number;
  textBlockHeightPx: number;
  contentH: number;
  canvasH: number;
}): { boxX1: number; boxY1: number; boxX2: number; boxY2: number } {
  const { frameRect, previewBoxHeightPx, textBlockHeightPx, contentH, canvasH } = params;
  if (contentH <= 0 || canvasH <= 0 || textBlockHeightPx <= 0 || previewBoxHeightPx <= 0) {
    return frameRect;
  }

  const scaleY = canvasH / contentH;
  const slackContent = (previewBoxHeightPx - textBlockHeightPx) / 2;
  if (slackContent <= 0.5) {
    return frameRect;
  }

  const deltaY = Math.round(slackContent * scaleY);
  const burnH = Math.max(1, Math.round(textBlockHeightPx * scaleY));

  let boxY1 = frameRect.boxY1 + deltaY;
  let boxY2 = boxY1 + burnH;

  if (boxY2 > canvasH) {
    boxY2 = canvasH;
    boxY1 = Math.max(0, boxY2 - burnH);
  }
  boxY1 = Math.max(0, boxY1);

  return {
    boxX1: frameRect.boxX1,
    boxY1,
    boxX2: frameRect.boxX2,
    boxY2,
  };
}

/**
 * 进入编辑页时：将后端压制用文字块坐标还原为预览外框坐标（与 compensateBurnBoxForBackendTopAlign 互逆）。
 */
export function reverseBurnBoxToDisplayFrame(params: {
  burnRect: { boxX1: number; boxY1: number; boxX2: number; boxY2: number };
  previewBoxHeightPx: number;
  textBlockHeightPx: number;
  contentH: number;
  canvasH: number;
}): { boxX1: number; boxY1: number; boxX2: number; boxY2: number } {
  const { burnRect, previewBoxHeightPx, textBlockHeightPx, contentH, canvasH } = params;
  if (contentH <= 0 || canvasH <= 0 || textBlockHeightPx <= 0 || previewBoxHeightPx <= 0) {
    return burnRect;
  }

  const scaleY = canvasH / contentH;
  const slackContent = (previewBoxHeightPx - textBlockHeightPx) / 2;
  const deltaY = slackContent <= 0.5 ? 0 : Math.round(slackContent * scaleY);
  const frameH = Math.max(1, Math.round(previewBoxHeightPx * scaleY));

  let boxY1 = burnRect.boxY1 - deltaY;
  let boxY2 = boxY1 + frameH;

  if (boxY2 > canvasH) {
    boxY2 = canvasH;
    boxY1 = Math.max(0, boxY2 - frameH);
  }
  boxY1 = Math.max(0, boxY1);

  return {
    boxX1: burnRect.boxX1,
    boxY1,
    boxX2: burnRect.boxX2,
    boxY2,
  };
}

/**
 * 合成视频接口请求体：
 * - boxX1/Y1/X2/Y2、fontSize、canvasWidth/Height 为字幕框坐标系（canvas）下的稳定 px
 * - videoWidth/Height 为视频文件真实像素
 * - borderWidth 与预览描边一致（基于 preview contain 高度），避免按 canvas 字号放大后过粗
 */
export function buildGenerateVideoRequestPayload(params: {
  taskId: number;
  snapshot: SubtitleStylePersistSnapshot | null;
  /** 预览 contain 画面高（与 SubtitleOverlayPlayer canvasInfo.h 一致） */
  contentDisplayHeight: number;
  /** 预览 contain 画面宽；与 previewBox* 联用时换算 box 坐标 */
  contentDisplayWidth?: number;
  /** 预览 Rnd 选框实际宽高（与 content 同坐标系）；有则 box 与画面一致，避免 heightRatio 偏大导致 Y 超出 canvas */
  previewBoxWidthPx?: number;
  previewBoxHeightPx?: number;
  /** 预览文字块高（与 SubtitleOverlayPlayer 绘制一致，content 坐标系）；用于压制顶对齐补偿 */
  textBlockHeightPx?: number;
  enabled?: boolean;
}): GenerateVideoRequestPayload | null {
  const {
    taskId,
    snapshot,
    contentDisplayHeight,
    contentDisplayWidth,
    previewBoxWidthPx,
    previewBoxHeightPx,
    textBlockHeightPx,
    enabled = true,
  } = params;
  if (!snapshot || contentDisplayHeight <= 0) return null;

  const backend = buildSubtitleStyleBackendPayload(snapshot);
  const fontSize = backend.FontSize;
  const borderWidth = computeGenerateVideoBorderWidth(snapshot.fontSizeRatio, contentDisplayHeight);
  const videoWidth = snapshot.intrinsicVideo.width;
  const videoHeight = snapshot.intrinsicVideo.height;
  const canvasWidth = backend.width;
  const canvasHeight = backend.height;

  const contentW = contentDisplayWidth ?? canvasWidth;
  const usePreviewRect =
    previewBoxWidthPx != null &&
    previewBoxHeightPx != null &&
    previewBoxWidthPx > 0 &&
    previewBoxHeightPx > 0 &&
    contentW > 0;

  let boxRect = usePreviewRect
    ? buildSubtitleBoxCanvasRectFromPreview({
        relativeCenter: snapshot.relativeCenter,
        previewBoxWidthPx,
        previewBoxHeightPx,
        contentW,
        contentH: contentDisplayHeight,
        canvasW: canvasWidth,
        canvasH: canvasHeight,
      })
    : {
        boxX1: backend.PosX,
        boxY1: backend.PosY,
        boxX2: backend.PosX + backend.boxWidth,
        boxY2: backend.PosY + backend.boxHeight,
      };

  if (
    usePreviewRect &&
    textBlockHeightPx != null &&
    textBlockHeightPx > 0 &&
    previewBoxHeightPx != null
  ) {
    boxRect = compensateBurnBoxForBackendTopAlign({
      frameRect: boxRect,
      previewBoxHeightPx,
      textBlockHeightPx,
      contentH: contentDisplayHeight,
      canvasH: canvasHeight,
    });
  }

  return {
    taskId,
    enabled,
    fontSize,
    fontColor: DEFAULT_GENERATE_VIDEO_FONT_COLOR,
    borderColor: DEFAULT_GENERATE_VIDEO_BORDER_COLOR,
    borderWidth,
    boxX1: boxRect.boxX1,
    boxY1: boxRect.boxY1,
    boxX2: boxRect.boxX2,
    boxY2: boxRect.boxY2,
    canvasWidth,
    canvasHeight,
    videoWidth,
    videoHeight,
  };
}

/** 由当前预览状态直接构建 generate-video 请求体 */
export function buildGenerateVideoRequestPayloadFromStyleState(params: {
  taskId: number;
  relativeCenter: { x: number; y: number };
  fontScale: number;
  boxWidthRatio: number;
  contentW: number;
  contentH: number;
  intrinsicVideoWidth: number;
  intrinsicVideoHeight: number;
  previewBoxWidthPx?: number;
  previewBoxHeightPx?: number;
  textBlockHeightPx?: number;
  enabled?: boolean;
}): GenerateVideoRequestPayload | null {
  const heightRatioOverride =
    params.previewBoxHeightPx != null && params.contentH > 0
      ? Math.min(1, params.previewBoxHeightPx / params.contentH)
      : undefined;
  const snapshot = buildSubtitleStylePersistSnapshot({
    relativeCenter: params.relativeCenter,
    fontScale: params.fontScale,
    boxWidthRatio: params.boxWidthRatio,
    contentW: params.contentW,
    contentH: params.contentH,
    intrinsicVideoWidth: params.intrinsicVideoWidth,
    intrinsicVideoHeight: params.intrinsicVideoHeight,
    heightRatioOverride,
  });
  return buildGenerateVideoRequestPayload({
    taskId: params.taskId,
    snapshot,
    contentDisplayWidth: params.contentW,
    contentDisplayHeight: params.contentH,
    previewBoxWidthPx: params.previewBoxWidthPx,
    previewBoxHeightPx: params.previewBoxHeightPx,
    textBlockHeightPx: params.textBlockHeightPx,
    enabled: params.enabled,
  });
}

export interface SubtitleStylePersistSnapshot {
  relativeCenter: { x: number; y: number };
  fontSizeRatio: number;
  widthRatio: number;
  heightRatio: number;
  /** 与 boxRatiosFromScale 一致，便于兼容只认 scale 的旧接口 */
  scale: number;
  previewReference: {
    width: number;
    height: number;
    baseFontPx: number;
  };
  intrinsicVideo: { width: number; height: number };
}

export function buildSubtitleStylePersistSnapshot(params: {
  relativeCenter: { x: number; y: number };
  /** 纵向比例：字号与框高 ∝ SUBTITLE_PREVIEW_BOX_HEIGHT_FRAC * fontScale */
  fontScale: number;
  /** 框宽 / contain 画面宽（左右手柄调节） */
  boxWidthRatio: number;
  contentW: number;
  contentH: number;
  intrinsicVideoWidth: number;
  intrinsicVideoHeight: number;
  /** 预览 Rnd 实际框高 / contentH；有则覆盖 SUBTITLE_PREVIEW_BOX_HEIGHT_FRAC * fontScale */
  heightRatioOverride?: number;
}): SubtitleStylePersistSnapshot | null {
  const {
    relativeCenter,
    fontScale,
    boxWidthRatio,
    contentW,
    contentH,
    intrinsicVideoWidth,
    intrinsicVideoHeight,
    heightRatioOverride,
  } = params;
  if (contentW <= 0 || contentH <= 0) return null;
  const drawPx = computePreviewSubtitleDrawFontPx({
    scale: fontScale,
    contentW,
    contentH,
    intrinsicVideoWidth,
    intrinsicVideoHeight,
  });
  const widthRatio = Math.min(Math.max(boxWidthRatio, 0), 1);
  const heightRatio =
    heightRatioOverride != null
      ? Math.min(1, Math.max(0, heightRatioOverride))
      : SUBTITLE_PREVIEW_BOX_HEIGHT_FRAC * fontScale;
  return {
    relativeCenter: { x: relativeCenter.x, y: relativeCenter.y },
    fontSizeRatio: fontDrawPxToFontSizeRatio(drawPx, contentH),
    widthRatio,
    heightRatio,
    scale: fontScale,
    previewReference: {
      width: SUBTITLE_PREVIEW_REFERENCE_WIDTH,
      height: SUBTITLE_PREVIEW_REFERENCE_HEIGHT,
      baseFontPx: SUBTITLE_PREVIEW_BASE_FONT_PX,
    },
    intrinsicVideo: { width: intrinsicVideoWidth, height: intrinsicVideoHeight },
  };
}

export function getContainFitTransform(
  sourceW: number,
  sourceH: number,
  targetW: number,
  targetH: number
): { scale: number; offsetX: number; offsetY: number; displayedW: number; displayedH: number } {
  const scale = Math.min(targetW / sourceW, targetH / sourceH);
  const displayedW = sourceW * scale;
  const displayedH = sourceH * scale;
  const offsetX = (targetW - displayedW) / 2;
  const offsetY = (targetH - displayedH) / 2;
  return { scale, offsetX, offsetY, displayedW, displayedH };
}

export function clampRoiToContainRect(
  roi: { x: number; y: number; width: number; height: number },
  offsetX: number,
  offsetY: number,
  displayedW: number,
  displayedH: number,
  minW = 60,
  minH = 30
): { x: number; y: number; width: number; height: number } {
  const w = Math.min(Math.max(roi.width, minW), displayedW);
  const h = Math.min(Math.max(roi.height, minH), displayedH);
  const x = Math.max(offsetX, Math.min(roi.x, offsetX + displayedW - w));
  const y = Math.max(offsetY, Math.min(roi.y, offsetY + displayedH - h));
  return { x, y, width: w, height: h };
}
